import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { showRewardedAd, fetchAdQuota } from "../lib/ads";

/**
 * "Watch ad for chips" button. Renders compact in the Lobby header. Shows the
 * remaining daily quota next to the action so users know how many freebies are
 * left. Disables itself when the quota is exhausted.
 */
export default function WatchAdButton({ onCredited }) {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null); // null | "playing" | "ok" | "err"
  const [error, setError] = useState("");
  const [quota, setQuota] = useState({ remaining: { chips_50: 5 }, caps: { chips_50: 5 } });
  const [lastReward, setLastReward] = useState(null);

  const refresh = async () => {
    try { setQuota(await fetchAdQuota()); } catch {}
  };

  useEffect(() => { refresh(); }, []);

  const remaining = quota?.remaining?.chips_50 ?? 0;
  const cap = quota?.caps?.chips_50 ?? 5;
  const exhausted = remaining <= 0;

  const watch = async () => {
    if (exhausted || busy) return;
    setBusy(true); setError(""); setPhase("playing");
    try {
      const res = await showRewardedAd("chips_50");
      setLastReward(res.chips || 50);
      setPhase("ok");
      onCredited?.(res.chips || 50);
      await refresh();
      setTimeout(() => setPhase(null), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Ad unavailable. Try again later.");
      setPhase("err");
      setTimeout(() => setPhase(null), 2500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={watch}
        disabled={exhausted || busy}
        className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 hover:bg-white/10"
        style={{
          border: `1px solid ${exhausted ? "rgba(255,255,255,0.10)" : "rgba(61,220,151,0.45)"}`,
          opacity: exhausted ? 0.55 : 1,
          cursor: exhausted ? "not-allowed" : "pointer",
          minHeight: 32,
        }}
        data-testid="watch-ad-btn"
        title={exhausted ? "Daily ad reward limit reached" : `Watch a 30-second ad for +50 chips (${remaining}/${cap} left today)`}
      >
        <span style={{ fontSize: 13 }}>📺</span>
        <span className="font-display font-bold" style={{ fontSize: 11 }}>+50</span>
        <span className="font-mono hidden sm:inline" style={{ fontSize: 10, color: exhausted ? "rgba(255,255,255,0.4)" : "#3DDC97" }}>
          {remaining}/{cap}
        </span>
      </button>

      <AnimatePresence>
        {phase === "playing" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
            data-testid="ad-overlay"
          >
            <div className="text-center">
              <div className="label-eyebrow mb-2" style={{ color: "#3DDC97", letterSpacing: 2 }}>SPONSORED · 30s</div>
              <div className="font-display font-black text-3xl mb-2">Playing rewarded ad…</div>
              <div className="text-white/55 text-xs">+50 chips when complete</div>
              <div className="mt-6 flex justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                    className="rounded-full"
                    style={{ width: 8, height: 8, background: "#3DDC97" }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
        {phase === "ok" && lastReward && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            className="fixed left-1/2 z-[130] glass rounded-full px-5 py-3 flex items-center gap-3"
            style={{
              top: 24, transform: "translateX(-50%)",
              border: "1px solid rgba(61,220,151,0.55)",
              boxShadow: "0 0 28px rgba(61,220,151,0.35)",
            }}
            data-testid="ad-reward-toast"
          >
            <span style={{ fontSize: 18 }}>🎉</span>
            <span className="font-display font-bold text-sm">
              Ad complete: <span style={{ color: "#FFD700" }}>+{lastReward} chips</span>
            </span>
          </motion.div>
        )}
        {phase === "err" && error && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            className="fixed left-1/2 z-[130] glass rounded-full px-5 py-3"
            style={{
              top: 24, transform: "translateX(-50%)",
              border: "1px solid rgba(255,92,92,0.5)",
              color: "#FF8A8A",
              fontSize: 13,
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
